import moment from 'moment';
import { i18n, tokens, Services } from "@simplybenefits/sb-ui-library";
import memberStatusMap from '../../common/memberStatusMap.js';

const now = new Date();
const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

/**
 * Generates advisor reports data with various sections and filters.
 * @returns {Object} Object containing advisor reports data.
 */
const advisorReportsData = () => ({
    [i18n.t(tokens.labels.reports.membersAndUsage)]: {
        [i18n.t(tokens.labels.reports.overAgeMemberListing)]: {
            title: i18n.t(tokens.labels.reports.overAgeMemberAnniversary),
            icon: 'elderly',
            description: i18n.t(tokens.labels.reports.viewAllMembersWhoWillBecomeOlder),
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment(),
                    endDate: moment().add(6, 'month')
                },
                age: {
                    type: 'number',
                    label: i18n.t(tokens.labels.reports.age),
                    isRequired: true,
                    value: 65
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.overAgeDependentListing)]: {
            title: i18n.t(tokens.labels.reports.overageDependent),
            icon: 'cake',
            description: i18n.t(tokens.labels.reports.viewAllCurrentDependentsOverage),
            testid: 'over-age-dependents',
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                }
            }
        },
        [i18n.t(tokens.labels.reports.dependentListing)]: {
            icon: 'groups',
            description: i18n.t(tokens.labels.reports.viewAllCurrentDependents),
            testid: 'dependent-listing',
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: [
                        i18n.t(tokens.labels.reports.all),
                        i18n.t(tokens.labels.employees.active),
                        i18n.t(tokens.labels.employees.accountDisabled)
                    ],
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.spendingAccountUsages)]: {
            title: i18n.t(tokens.labels.reports.hsaLsaUsages),
            icon: 'receipt',
            description: i18n.t(tokens.labels.reports.viewSpendingAccountPremiums),
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                member: true,
                type: {
                    type: 'select',
                    label: i18n.t(tokens.labels.coverage.benefit),
                    options: Services.selectFieldOptions.spendingAccountOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.hsaBalance)]: {
            icon: 'healthAndSafety',
            description: i18n.t(tokens.labels.reports.viewCurrentHsaBalancesPerMember),
            testid: 'hsa-balance',
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.lsaBalance)]: {
            icon: 'shoppingCart',
            description: i18n.t(tokens.labels.reports.viewCurrentLsaBalancesPerMember),
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.certificateListing)]: {
            icon: 'assignment',
            description: i18n.t(tokens.labels.reports.getDataOnEachMember),
            testid: 'certificate-listing',
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.nemEligibilityReportTitle)]: {
            title: i18n.t(tokens.labels.reports.nemEligibilityReportTitle),
            icon: 'grading',
            description: i18n.t(tokens.labels.reports.nemEligibilityReportDesc),
            testid: 'nem-eligibility-report',
            filters: {
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes',
                    isRequired: true
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions',
                    isRequired: true
                },
                benefit: {
                    type: 'select',
                    label: 'Benefit',
                    options: [
                        'All',
                        'Life',
                        'Accidental Death Dismemberment',
                        'Short Term Disability',
                        'Long Term Disability'
                    ],
                    value: 'All',
                    isRequired: true
                },
                status: {
                    type: 'select',
                    label: 'Status',
                    options: [
                        'All',
                        ...Object.values(memberStatusMap)
                    ],
                    value: 'All',
                    isRequired: true
                }
            }
        },
        'Paid Claim Summary': {
            icon: 'barCharts',
            rules: [{
                type: 'users',
                min: 10
            }],
            description: 'Pull a report to view a summary of benefit utilization based on claims.',
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment(),
                    isRequired: true
                },
                dateRangeType: {
                    type: 'bubble-select',
                    label: 'Date Range Filter Type',
                    options: [ 'Service Date', 'Paid Date' ],
                    value: 'Service Date',
                    isRequired: true
                },
                benefit: {
                    type: 'select',
                    label: 'Benefit',
                    options: [
                        'All',
                        'Dental',
                        'Drugs',
                        'Health',
                        'Paramedicals',
                        'Vision'
                    ],
                    value: 'All',
                    isRequired: true
                },
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes'
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                }
            }
        }
    },
    [i18n.t(tokens.labels.reports.financial)]: {
        [i18n.t(tokens.labels.reports.asoMonthlySummary)]: {
            icon: 'adminPanelSettings',
            description: i18n.t(tokens.labels.reports.viewAsoMonthlySummary),
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                from: {
                    type: 'month',
                    value: firstOfMonth
                },
                to: {
                    type: 'month',
                    value: firstOfMonth
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                benefit: {
                    type: 'select',
                    label: i18n.t(tokens.labels.coverage.benefit),
                    options: Services.selectFieldOptions.healthDentalOptions(),
                    value: i18n.t(tokens.dropdowns.healthDentalOptions.both)
                }
            }
        },
        [i18n.t(tokens.labels.reports.taxableBenefit)]: {
            icon: 'monetizationOn',
            description: i18n.t(tokens.labels.reports.viewYearlyPremium),
            testid: 'taxable-benefit',
            titleExpansionKeys: [ 'groupNum', 'company', 'division' ],
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                // Members select with member options
                status: {
                    type: 'select',
                    label: i18n.t(tokens.labels.employees.status),
                    options: Services.selectFieldOptions.memberStatusOptions(),
                    value: i18n.t(tokens.labels.reports.all)
                }
            }
        },
        [i18n.t(tokens.labels.reports.lossRatio)]: {
            icon: 'compareArrows',
            description: i18n.t(tokens.labels.reports.viewLossRatio),
            testid: 'loss-ratio',
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                }
            }
        },
        [i18n.t(tokens.labels.reports.ltdReport)]: {
            title: i18n.t(tokens.labels.reports.ltd),
            icon: 'dateRange',
            description: i18n.t(tokens.labels.reports.viewLtdDisabilityReport),
            testid: 'ltd-report',
            filters: {
                division: {
                    type: 'select',
                    label: i18n.t(tokens.titles.division),
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                class: {
                    type: 'select',
                    label: i18n.t(tokens.titles.class),
                    options: [ 'All Classes' ],
                    value: i18n.t(tokens.labels.reports.allClasses)
                }
            }
        }
    }
});

export default advisorReportsData;